import { useEffect, useState } from 'react';

/**
 * Phase 5.X-v1-parity-polish Feature 1 — user-scope toggle controlling whether
 * document lists render the canonical `FileName` (rename-target name) or the
 * `OriginalFileName` (as-uploaded). V1 wired this through its SETTINGS_SCHEMA.
 *
 * V2's settings backend is generic key/value (no schema validation), and the
 * deep audit (§1.11) flagged that the SETTINGS_SCHEMA itself is missing in
 * V2 — its restoration is a separate phase (`5.X-settings-schema-ui`). Until
 * that ships, this preference is stored as a per-browser localStorage key,
 * matching the existing pattern for `dashboardCompactMode`. When the
 * settings-schema phase lands, this hook should be flipped to read from
 * `v2.settings.listAll()` and the key promoted to the schema.
 *
 * Default: `false` (show canonical FileName) per the prompt's guardrail #3.
 */

export const SHOW_ORIGINAL_FILENAMES_LS_KEY = 'showOriginalFilenames';

function loadPref(): boolean {
  try {
    return localStorage.getItem(SHOW_ORIGINAL_FILENAMES_LS_KEY) === 'true';
  } catch {
    return false;
  }
}

function savePref(value: boolean): void {
  try {
    localStorage.setItem(SHOW_ORIGINAL_FILENAMES_LS_KEY, String(value));
  } catch {
    /* ignore — Safari private mode etc. */
  }
}

export interface UseShowOriginalFilenamesResult {
  /** True when the user wants OriginalFileName shown in document lists. */
  readonly showOriginal: boolean;
  /** Setter that writes through to localStorage. */
  readonly setShowOriginal: (next: boolean) => void;
}

export function useShowOriginalFilenames(): UseShowOriginalFilenamesResult {
  const [showOriginal, setShowOriginalState] = useState<boolean>(loadPref);

  // Cross-tab sync — if the user toggles in another tab/window, mirror it here.
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key !== SHOW_ORIGINAL_FILENAMES_LS_KEY) return;
      setShowOriginalState(e.newValue === 'true');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setShowOriginal = (next: boolean): void => {
    savePref(next);
    setShowOriginalState(next);
  };

  return { showOriginal, setShowOriginal };
}

/**
 * Resolve the filename to display for a Document row, honoring the toggle.
 *
 * Falls back to `FileName` when `OriginalFileName` is missing/null — i.e.,
 * post-backfill all rows should have OriginalFileName populated, but defensive
 * code is cheap.
 */
export function resolveDocumentDisplayName(
  doc: { FileName: string; OriginalFileName?: string | null },
  showOriginal: boolean,
): string {
  if (!showOriginal) return doc.FileName;
  return doc.OriginalFileName ?? doc.FileName;
}
